import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import * as moment from 'moment';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { VACATION_ERROR_CODE, VACATION_TYPE, VacationErrorCode, VacationWarningCode } from './vacation-api.constants';
import { VacationApiService } from './vacation-api.service';
import {
  VACATION_BOOKING_DATE_UNIT,
  VACATION_BOOKING_PANEL_CONFIG,
  VACATION_BOOKING_PANEL_KEY,
  VACATION_BOOKING_YEAR_UNIT,
  VACATION_ENTRY_STATUS_CLASS,
  VACATION_ENTRY_STATUS_HINT_TRANSLATION,
  VACATION_ENTRY_STATUS_TRANSLATION,
  VACATION_ERROR_TRANSLATION,
  VACATION_SEASON_TRANSLATION,
  VACATION_TYPE_TRANSLATION,
  VACATION_WARNING_TRANSLATION
} from './vacation-booking-panel.constants';
import {
  VacationApiErrorBody,
  VacationBalanceResponse,
  VacationBookingPreviewResponse,
  VacationBookingRequest,
  VacationBookingResponse,
  VacationDateRangeSelection,
  VacationEntriesResponse,
  VacationEntry
} from './vacation.models';

const MINIMUM_PLANFIX_TASK_ID = 1;

@Component({
  selector: 'app-vacation-booking-panel',
  templateUrl: './vacation-booking-panel.component.html',
  styleUrls: ['./vacation-booking-panel.component.css']
})
export class VacationBookingPanelComponent implements OnInit, OnDestroy {

  @Output() taskCreated = new EventEmitter<number>();

  readonly config = VACATION_BOOKING_PANEL_CONFIG;
  readonly paidLeaveType = VACATION_TYPE.PAID_LEAVE;
  readonly statusClass = VACATION_ENTRY_STATUS_CLASS;
  readonly statusHintTranslation = VACATION_ENTRY_STATUS_HINT_TRANSLATION;
  readonly statusTranslation = VACATION_ENTRY_STATUS_TRANSLATION;
  readonly seasonTranslation = VACATION_SEASON_TRANSLATION;
  readonly typeTranslation = VACATION_TYPE_TRANSLATION;
  readonly errorTranslation = VACATION_ERROR_TRANSLATION;
  readonly warningTranslation = VACATION_WARNING_TRANSLATION;

  request: VacationBookingRequest = {
    from: VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT,
    to: VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT,
    description: VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT
  };
  balance: VacationBalanceResponse;
  preview: VacationBookingPreviewResponse;
  entries: VacationEntry[] = [];
  minimumDate = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  maximumDate = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  balanceErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  entriesErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  formErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  successKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  entryActionId = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  pendingCancellationId = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  balanceLoading = false;
  entriesLoading = false;
  previewLoading = false;
  bookingCreating = false;
  canCreate = false;
  canLoadMoreEntries = false;
  private entriesPage = VACATION_BOOKING_PANEL_CONFIG.FIRST_PAGE;
  private destroy$ = new Subject<void>();

  constructor(private vacationApiService: VacationApiService) {}

  ngOnInit(): void {
    const today = moment();
    this.minimumDate = today.clone()
      .add(VACATION_BOOKING_PANEL_CONFIG.MINIMUM_LEAD_DAYS, VACATION_BOOKING_DATE_UNIT)
      .format(VACATION_BOOKING_PANEL_CONFIG.DATE_FORMAT);
    this.maximumDate = today.clone()
      .add(VACATION_BOOKING_PANEL_CONFIG.MAXIMUM_ADVANCE_YEARS, VACATION_BOOKING_YEAR_UNIT)
      .format(VACATION_BOOKING_PANEL_CONFIG.DATE_FORMAT);
    this.loadBalance();
    this.loadEntries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  invalidatePreview(): void {
    this.preview = undefined;
    this.canCreate = false;
    this.formErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.successKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  }

  applyCalendarSelection(selection: VacationDateRangeSelection): void {
    this.request.from = selection.from;
    this.request.to = selection.to;
    this.invalidatePreview();
  }

  previewBooking(): void {
    if (!this.request.from || !this.request.to || this.request.from > this.request.to) {
      return;
    }
    this.previewLoading = true;
    this.formErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.vacationApiService.previewBooking(this.request)
      .takeUntil(this.destroy$)
      .subscribe(
        response => {
          this.preview = response;
          this.canCreate = response.canBook;
          this.previewLoading = false;
        },
        error => this.failFormRequest(error)
      );
  }

  createBooking(): void {
    if (!this.preview || !this.canCreate || this.bookingCreating) {
      return;
    }
    this.bookingCreating = true;
    this.formErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.vacationApiService.createBooking(this.request)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.acceptCreatedBooking(),
        error => this.failFormRequest(error)
      );
  }

  confirmBooking(entryId: string): void {
    if (this.entryActionId) {
      return;
    }
    this.startEntryAction(entryId);
    this.vacationApiService.confirmBooking(entryId)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.acceptConfirmedBooking(response),
        error => this.failEntryAction(error)
      );
  }

  requestCancellation(entryId: string): void {
    this.pendingCancellationId = entryId;
  }

  dismissCancellation(): void {
    this.pendingCancellationId = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  }

  cancelBooking(entryId: string): void {
    if (this.entryActionId) {
      return;
    }
    this.startEntryAction(entryId);
    this.vacationApiService.cancelBooking(entryId)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.completeEntryAction(VACATION_BOOKING_PANEL_KEY.BOOKING_CANCELLED),
        error => this.failEntryAction(error)
      );
  }

  loadBalance(): void {
    this.balanceLoading = true;
    this.balanceErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.vacationApiService.getMyBalance(this.currentDate())
      .takeUntil(this.destroy$)
      .subscribe(
        response => {
          this.balance = response;
          this.balanceLoading = false;
        },
        error => {
          this.balanceLoading = false;
          this.balanceErrorKey = this.resolveErrorKey(
            error,
            VACATION_BOOKING_PANEL_KEY.BALANCE_LOAD_ERROR
          );
        }
      );
  }

  loadEntries(): void {
    this.entries = [];
    this.entriesPage = VACATION_BOOKING_PANEL_CONFIG.FIRST_PAGE;
    this.fetchEntries(this.entriesPage);
  }

  loadMoreEntries(): void {
    if (!this.canLoadMoreEntries || this.entriesLoading) {
      return;
    }
    this.fetchEntries(this.entriesPage + VACATION_BOOKING_PANEL_CONFIG.PAGE_STEP);
  }

  trackByEntryId(index: number, entry: VacationEntry): string {
    return entry.id;
  }

  trackByCode(index: number, code: VacationErrorCode | VacationWarningCode): string {
    return code;
  }

  private fetchEntries(page: number): void {
    this.entriesLoading = true;
    this.entriesErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.vacationApiService.getMyEntries(page, VACATION_BOOKING_PANEL_CONFIG.PAGE_SIZE)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.acceptEntries(response, page),
        error => {
          this.entriesLoading = false;
          this.entriesErrorKey = this.resolveErrorKey(
            error,
            VACATION_BOOKING_PANEL_KEY.ENTRIES_LOAD_ERROR
          );
        }
      );
  }

  private acceptEntries(response: VacationEntriesResponse, page: number): void {
    this.entries = page === VACATION_BOOKING_PANEL_CONFIG.FIRST_PAGE
      ? response.items
      : this.entries.concat(response.items);
    this.entriesPage = response.page;
    this.canLoadMoreEntries = response.page + VACATION_BOOKING_PANEL_CONFIG.PAGE_STEP
      < response.totalPages;
    this.entriesLoading = false;
  }

  private acceptCreatedBooking(): void {
    this.bookingCreating = false;
    this.request = {
      from: VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT,
      to: VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT,
      description: VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT
    };
    this.invalidatePreview();
    this.successKey = VACATION_BOOKING_PANEL_KEY.BOOKING_CREATED;
    this.loadBalance();
    this.loadEntries();
  }

  private startEntryAction(entryId: string): void {
    this.entryActionId = entryId;
    this.formErrorKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.successKey = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
  }

  private acceptConfirmedBooking(response: VacationBookingResponse): void {
    this.completeEntryAction(VACATION_BOOKING_PANEL_KEY.BOOKING_CONFIRMED);
    if (Number.isInteger(response.planfixTaskId) && response.planfixTaskId >= MINIMUM_PLANFIX_TASK_ID) {
      this.taskCreated.emit(response.planfixTaskId);
    }
  }

  private completeEntryAction(successKey: string): void {
    this.entryActionId = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.pendingCancellationId = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.successKey = successKey;
    this.loadBalance();
    this.loadEntries();
  }

  private failFormRequest(error: HttpErrorResponse): void {
    this.previewLoading = false;
    this.bookingCreating = false;
    this.preview = undefined;
    this.canCreate = false;
    this.formErrorKey = this.resolveErrorKey(error, VACATION_BOOKING_PANEL_KEY.FORM_ERROR);
  }

  private failEntryAction(error: HttpErrorResponse): void {
    this.entryActionId = VACATION_BOOKING_PANEL_CONFIG.EMPTY_TEXT;
    this.formErrorKey = this.resolveErrorKey(error, VACATION_BOOKING_PANEL_KEY.ACTION_ERROR);
    const body = error.error as VacationApiErrorBody;
    if (body && body.errorCode === VACATION_ERROR_CODE.APPROVAL_ROUTE_UNVERIFIED) {
      this.loadEntries();
    }
  }

  private currentDate(): string {
    return moment().format(VACATION_BOOKING_PANEL_CONFIG.DATE_FORMAT);
  }

  private resolveErrorKey(error: HttpErrorResponse, fallbackKey: string): string {
    const body = error.error as VacationApiErrorBody;
    if (!body || !body.errorCode) {
      return fallbackKey;
    }
    return VACATION_ERROR_TRANSLATION[body.errorCode] || fallbackKey;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/vacation/vacation-booking-panel.component.ts